// FurnitureInpaintingApp.jsx - Unified component for all inpainting steps
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {motion} from 'framer-motion';
import apiService from '../../services/api';
import {FaArrowRight, FaDownload, FaInfoCircle, FaMagic, FaPencilAlt, FaRedo} from 'react-icons/fa';

// UI Components
import {Card, CardContent} from '../../components/ui/card';
import {Button} from '../../components/ui/button';
import {Spinner} from '../../components/Spinner';
import CanvasEditor from '../../components/CanvasEditor';
import ImagePreview from '../../components/ImagePreview';

const FurnitureInpaintingApp = () => {
	const {t} = useTranslation();
	const roomUploadRef = useRef(null);
	
	// States for all workflow data
	const [roomImage, setRoomImage] = useState(null);
	const [maskImage, setMaskImage] = useState(null);
	const [resultImage, setResultImage] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	
	// Canvas drawing states
	const [brushSize, setBrushSize] = useState(20);
	const [eraseMode, setEraseMode] = useState(false);
	const [imageObj, setImageObj] = useState(null);
	
	// Generation parameter states
	const [prompt, setPrompt] = useState('');
	const [negativePrompt, setNegativePrompt] = useState('');
	const [stylePreset, setStylePreset] = useState('');
	
	// UI state
	const [activePanel, setActivePanel] = useState('upload'); // upload, draw, generate, result
	
	// Available style presets
	const stylePresets = [
		{value: '', label: t('common.noPreset')},
		{value: 'photographic', label: t('common.stylePresets.photographic')},
		{value: 'digital-art', label: t('common.stylePresets.digitalArt')},
		{value: '3d-model', label: t('common.stylePresets.3dModel')},
		{value: 'cinematic', label: t('common.stylePresets.cinematic')},
		{value: 'anime', label: t('common.stylePresets.anime')},
		{value: 'enhance', label: t('common.stylePresets.enhance')}
	];
	
	// Example prompts
	const examplePrompts = [
		t('generateStep.prompts.leatherSofa'),
		t('generateStep.prompts.tableChairs'),
		t('generateStep.prompts.coffeeTable'),
		t('generateStep.prompts.painting'),
		t('generateStep.prompts.plant')
	];
	
	// Initialize image object when room image changes
	useEffect(() => {
		if (roomImage && roomImage.src) {
			const img = new Image();
			img.onload = () => {
				setImageObj(img);
			};
			img.src = roomImage.src;
		}
	}, [roomImage]);
	
	// Handle room image upload
	const handleRoomImageUpload = (file) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const img = new Image();
			img.onload = () => {
				setRoomImage({
					file: file,
					src: e.target.result,
					width: img.width,
					height: img.height
				});
				
				// Clear mask when new room is uploaded
				setMaskImage(null);
				setActivePanel('draw');
			};
			img.src = e.target.result;
		};
		reader.readAsDataURL(file);
	};
	
	// Handle mask image upload (alternative to drawing)
	const handleMaskImageUpload = (file) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const img = new Image();
			img.onload = () => {
				setMaskImage({
					file: file,
					src: e.target.result,
					width: img.width,
					height: img.height
				});
				setActivePanel('generate');
			};
			img.src = e.target.result;
		};
		reader.readAsDataURL(file);
	};
	
	// Handle mask drawing save
	const handleSaveMask = async (maskDataUrl) => {
		setIsLoading(true);
		
		try {
			// Convert data URL to a file object
			const response = await fetch(maskDataUrl);
			const blob = await response.blob();
			const file = new File([blob], 'mask.png', {type: 'image/png'});
			
			// Save the mask to state
			setMaskImage({
				file: file,
				src: maskDataUrl,
				width: roomImage.width,
				height: roomImage.height,
				serverPath: null // Will be set after API call
			});
			
			// Call API to save the mask
			const result = await apiService.saveMask(maskDataUrl);
			
			if (result.success) {
				// Update the mask with the server path
				setMaskImage(prev => ({
					...prev,
					serverPath: result.maskFilename
				}));
				
				setActivePanel('generate');
			} else {
				throw new Error(result.message || 'Failed to save mask');
			}
		} catch (error) {
			console.error('Error saving mask:', error);
			alert(t('errors.maskSaveError'));
		} finally {
			setIsLoading(false);
		}
	};
	
	// Handle generation
// Handle generation
	const handleGenerate = async () => {
		console.log('[DEBUG] handleGenerate called');
		setIsLoading(true);
		setResultImage(null); // Reset previous result
		
		try {
			// Ensure we have the required files
			if (!roomImage || !roomImage.file) {
				throw new Error('Room image is missing');
			}
            console.log('[DEBUG] Room image file:', roomImage.file);
			
			// Prepare API parameters
			const params = {
				prompt,
				negativePrompt,
				stylePreset,
				mask_area: 'center' // Default value if no mask is drawn
			};
            console.log('[DEBUG] Generation params:', params);
			
			// Check if we're using a drawn mask or uploaded mask
			let maskFile = null;
			if (maskImage && maskImage.file) {
				maskFile = maskImage.file;
                console.log('[DEBUG] Using mask file:', maskFile);
			} else {
                console.log('[DEBUG] No mask file provided to API');
			}
			
			// Call inpainting API
            console.log('[DEBUG] Calling apiService.inpaintFurniture');
			const result = await apiService.inpaintFurniture(
				roomImage.file,
				maskFile,
				params
			);
            console.log('[DEBUG] API Result:', result);
			
			if (result.success && result.outputPath) {
                console.log('[DEBUG] API call successful. Output path:', result.outputPath);
				// Convert the output path to a full URL if it's a relative path
				let resultUrl = apiService.getFullUrl(result.outputPath);
                console.log('[DEBUG] Constructed result URL:', resultUrl);
				
				// Set result image immediately to change the panel
				// **IMPORTANT**: Log the state *before* setting it
				console.log('[DEBUG] Current resultImage state (before setting):', resultImage);
				const newResultState = {
					src: resultUrl,
					width: roomImage.width, // Use room dimensions initially
					height: roomImage.height
				};
				setResultImage(newResultState);
				// **IMPORTANT**: Log the state *after* setting it (may not reflect immediately due to async nature)
				console.log('[DEBUG] Setting resultImage state to:', newResultState);

				
				// Change to result panel
				console.log('[DEBUG] Setting active panel to: result');
				setActivePanel('result');
				
				// Load image in background to get actual dimensions (async, happens after panel change)
				console.log('[DEBUG] Starting background image load for dimensions...');
				const img = new Image();
				img.onload = () => {
                    console.log('[DEBUG] Background image loaded successfully. Updating dimensions.');
					// Update result image with actual dimensions
					setResultImage(prev => {
						const updatedState = { ...prev, width: img.width, height: img.height };
						console.log('[DEBUG] Updating resultImage state with actual dimensions:', updatedState);
						return updatedState;
					});
				};
				img.onerror = (error) => {
					console.error('[DEBUG] Failed to load generated image in background:', error);
					alert(t('errors.imageLoadError'));
					// Optionally reset result state if image fails to load?
					// setResultImage(null);
                    // setActivePanel('generate'); // Or back to generate?
				};
				img.src = resultUrl; // Start loading
			} else {
                console.error('[DEBUG] API call failed or missing outputPath.');
				throw new Error(result.message || 'Failed to generate image or missing output path');
			}
		} catch (error) {
			console.error('[DEBUG] Generation error:', error);
			alert(t('errors.generationError') + ': ' + error.message);
			setResultImage(null); // Ensure result is cleared on error
            // Optionally switch back to generate panel on error?
            // setActivePanel('generate');
		} finally {
            console.log('[DEBUG] handleGenerate finished');
			setIsLoading(false);
		}
	};
	// Handle download result
	const handleDownload = () => {
		if (!resultImage || !resultImage.src) {
			console.error('No result image to download');
			return;
		}
		
		const link = document.createElement('a');
		link.href = resultImage.src;
		link.download = 'inpainted-room.png';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	
	// Handle reset everything
	const handleStartOver = () => {
		setRoomImage(null);
		setMaskImage(null);
		setResultImage(null);
		setPrompt('');
		setNegativePrompt('');
		setStylePreset('');
		setActivePanel('upload');
	};
	
	// Handle reset room image
	const handleResetRoomImage = () => {
		setRoomImage(null);
		setMaskImage(null);
		setTimeout(() => {
			if (roomUploadRef.current) {
				roomUploadRef.current.click();
			}
		}, 100);
	};
	
	// Render the left panel with instructions and tips
	const renderLeftPanel = () => (
		<div
			className="left-panel h-full overflow-y-auto p-6 flex flex-col bg-card/10 backdrop-blur-sm rounded-xl border border-border/30">
			<div className="panel-header mb-6">
				<h2 className="text-2xl font-semibold text-primary">{t('features.furnitureInpainting.title')}</h2>
			</div>
			
			{/* Contextual instructions based on active panel */}
			<div className="panel-content flex-1">
				{activePanel === 'upload' && (
					<div className="bg-card/30 backdrop-blur-sm p-6 rounded-xl mb-6 border border-border/50 shadow-sm">
						<div className="flex items-start gap-4">
							<div className="bg-primary/10 p-3 rounded-full">
								<FaInfoCircle className="text-xl text-primary"/>
							</div>
							<div>
								<h3 className="text-xl font-medium mb-2 text-primary">{t('common.howItWorks')}</h3>
								<p className="text-text-light">
									{t('uploadStep.howItWorksDescription')}
								</p>
							</div>
						</div>
					</div>
				)}
				
				{activePanel === 'draw' && (
					<>
						<div className="flex items-start gap-4 mb-4">
							<div className="bg-primary/10 p-3 rounded-full">
								<FaPencilAlt className="text-xl text-primary"/>
							</div>
							<div>
								<h3 className="text-xl font-medium mb-2 text-primary">{t('drawStep.title')}</h3>
								<p className="text-text-light">
									{t('drawStep.description')}
								</p>
							</div>
						</div>
					</>
				)}
				
				{activePanel === 'generate' && (
					<>
						<div className="flex items-start gap-4 mb-4">
							<div className="bg-primary/10 p-3 rounded-full">
								<FaMagic className="text-xl text-primary"/>
							</div>
							<div>
								<h3 className="text-xl font-medium mb-2 text-primary">{t('generateStep.title')}</h3>
								<p className="text-text-light">
									{t('generateStep.description')}
								</p>
							</div>
						</div>
						
						<Card variant="glass" className="mb-6">
							<CardContent className="p-4">
								<div className="flex items-start gap-2">
									<FaInfoCircle className="text-primary mt-1 flex-shrink-0"/>
									<p className="text-sm">
										<strong>{t('common.tips')}:</strong> {t('generateStep.tips')}
									</p>
								</div>
							</CardContent>
						</Card>
					</>
				)}
				
				{activePanel === 'result' && (
					<>
						<div className="flex items-start gap-4 mb-4">
							<div className="bg-primary/10 p-3 rounded-full">
								<FaDownload className="text-xl text-primary"/>
							</div>
							<div>
								<h3 className="text-xl font-medium mb-2 text-primary">{t('resultStep.title')}</h3>
								<p className="text-text-light">
									{t('resultStep.description') || t('common.downloadDescription')}
								</p>
							</div>
						</div>
					</>
				)}
			</div>
			
			{/* Preview images in the left panel */}
			<div className="preview-images mt-auto">
				{roomImage && (
					<div className="mb-4">
						<p className="text-sm font-medium mb-2 text-text-light">{t('common.originalImage')}:</p>
						<div className="relative">
							<ImagePreview
								src={roomImage.src}
								alt="Room"
								className="w-full h-52 object-cover rounded-lg border border-border/50"
							/>
						</div>
					</div>
				)}
				
				{maskImage && (
					<div className="mb-4">
						<p className="text-sm font-medium mb-2 text-text-light">{t('uploadStep.maskImage')}:</p>
						<ImagePreview
							src={maskImage.src}
							alt="Mask"
							className="w-full h-52 object-cover rounded-lg border border-border/50"
						/>
					</div>
				)}
			</div>
		</div>
	);
	
	// Render the center panel with canvas editor or result
	const renderCenterPanel = () => (
		<div className="center-panel h-full overflow-hidden flex flex-col">
			{activePanel === 'upload' && (
				<div
					className="upload-area h-full flex items-center justify-center border-2 border-dashed border-border/50 rounded-xl hover:border-primary/50 transition-colors">
					<div className="text-center p-8">
						<div className="mb-4">
							<div
								className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor"
									 viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
										  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
								</svg>
							</div>
							<h3 className="text-lg font-medium text-text">{t('uploadStep.roomImage')}</h3>
							<p className="text-text-light mt-2">{t('common.uploadImageDescription')}</p>
						</div>
						
						<input
							type="file"
							ref={roomUploadRef}
							className="hidden"
							accept="image/png,image/jpeg,image/jpg,image/webp"
							onChange={(e) => e.target.files[0] && handleRoomImageUpload(e.target.files[0])}
						/>
						
						<Button
							onClick={() => roomUploadRef.current.click()}
							className="px-6"
						>
							{t('common.upload')}
						</Button>
					</div>
				</div>
			)}
			
			{activePanel === 'draw' && imageObj && (
				<div className="canvas-container h-full bg-card rounded-xl shadow-md overflow-hidden">
					<CanvasEditor
						image={imageObj}
						brushSize={brushSize}
						setBrushSize={setBrushSize}
						eraseMode={eraseMode}
						setEraseMode={setEraseMode}
						onSaveMask={handleSaveMask}
					/>
				</div>
			)}
			
			{activePanel === 'generate' && (
				<div
					className="result-container h-full flex items-center justify-center bg-card/20 backdrop-blur-sm rounded-xl border border-border/30">
					<div className="placeholder-content text-center p-6">
						<div
							className="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-6">
							<FaMagic className="w-8 h-8 text-primary/30"/>
						</div>
						<h3 className="text-xl font-medium text-text-light mb-2">{t('generateStep.title')}</h3>
						<p className="text-text-light/70 max-w-md">
							{t('generateStep.description')}
						</p>
					</div>
				</div>
			)}
			
			{activePanel === 'result' && (
				<div
					className="result-container h-full flex items-center justify-center bg-card/20 backdrop-blur-sm rounded-xl border border-border/30 overflow-hidden">
					{resultImage && resultImage.src ? ( // Check if resultImage and src exist
						<div className="result-preview w-full h-full flex items-center justify-center p-2">
							{/* Direct image rendering instead of using ImagePreview component */}
							<img
								src={resultImage.src}
								alt="Result Preview"
								className="max-w-full max-h-full object-contain rounded-lg"
							/>
						</div>
					) : (
						<div className="flex items-center justify-center">
							<Spinner size="lg"/>
						</div>
					)}
				</div>
			)}
		</div>
	);
	// Render the right panel with input controls
	const renderRightPanel = () => (
		<div
			className="right-panel h-full overflow-y-auto p-6 flex flex-col bg-card/10 backdrop-blur-sm rounded-xl border border-border/30">
			{activePanel === 'upload' && (
				<div className="flex-1 flex items-center justify-center">
					<div className="text-center">
						<div
							className="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-6">
							<FaArrowRight className="w-8 h-8 text-primary/30"/>
						</div>
						<h3 className="text-xl font-medium text-text-light mb-2">{t('uploadStep.uploadNextStepHint')}</h3>
						<p className="text-text-light/70 max-w-md">
							{t('uploadStep.uploadNextStepHintDescription')}
						</p>
					</div>
				</div>
			)}
			
			{activePanel === 'draw' && (
				<div className="draw-controls flex-1 flex flex-col">
					<Card variant="glass" className="mb-6">
						<CardContent className="p-4">
							<div className="flex items-start gap-2">
								<FaInfoCircle className="text-primary mt-1 flex-shrink-0"/>
								<div>
									<p className="font-medium mb-1">{t('common.tips')}:</p>
									<ul className="list-disc pl-5 space-y-1 text-sm">
										<li>
											{t('drawStep.tips.useDrawTool', {
												bold: (text) => <span className="font-medium">{text}</span>
											})}
										</li>
										<li>
											{t('drawStep.tips.useEraseTool', {
												bold: (text) => <span className="font-medium">{text}</span>
											})}
										</li>
										<li>
											{t('drawStep.tips.adjustBrushSize')}
										</li>
									</ul>
								</div>
							</div>
						</CardContent>
					</Card>
					{/* Button to save mask is inside CanvasEditor */}
				</div>
			)}
			
			{activePanel === 'generate' && (
				<div className="generate-form flex-1 flex flex-col">
					<h3 className="text-xl font-medium mb-4 text-primary">{t('generateStep.promptTitle') || 'Describe Your Furniture'}</h3>
					
					<div className="form-group mb-4">
						<label htmlFor="prompt"
							   className="block text-sm font-medium mb-2 text-text">{t('generateStep.promptLabel')}:</label>
						<textarea
							id="prompt"
							className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
							value={prompt}
							onChange={(e) => setPrompt(e.target.value)}
							placeholder={t('generateStep.promptPlaceholder')}
							rows={4}
							required
						/>
						
						<div className="mt-4">
							<p className="text-sm font-medium mb-2 text-text-light">{t('generateStep.promptExamples').split(':')[0]}:</p>
							<div className="flex flex-wrap gap-2">
								{examplePrompts.map((example, index) => (
									<button
										key={index}
										type="button"
										className="text-xs bg-primary/10 hover:bg-primary/20 text-primary px-3 py-1 rounded-full transition"
										onClick={() => setPrompt(example)}
									>
										{example}
									</button>
								))}
							</div>
						</div>
					</div>
					
					<div className="form-group mb-4">
						<label htmlFor="negativePrompt"
							   className="block text-sm font-medium mb-2 text-text">{t('common.negativePrompt')}:</label>
						<textarea
							id="negativePrompt"
							className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
							value={negativePrompt}
							onChange={(e) => setNegativePrompt(e.target.value)}
							placeholder={t('common.negativePromptPlaceholder')}
							rows={2}
						/>
					</div>
					
					<div className="form-group mb-6">
						<label htmlFor="stylePreset"
							   className="block text-sm font-medium mb-2 text-text">{t('common.stylePreset')}:</label>
						<select
							id="stylePreset"
							className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
							value={stylePreset}
							onChange={(e) => setStylePreset(e.target.value)}
						>
							{stylePresets.map((preset) => (
								<option key={preset.value} value={preset.value}>
									{preset.label}
								</option>
							))}
						</select>
					</div>
					
					<div className="mt-auto">
						<Button
							className="w-full"
							onClick={handleGenerate}
							disabled={!prompt.trim() || isLoading || !roomImage} // Disable if no image or prompt
						>
							{isLoading ?
								<><Spinner size="sm"/> {t('common.processing')}</> :
								<><FaMagic className="mr-2"/> {t('generateStep.generate')}</>
							}
						</Button>
					</div>
				</div>
			)}
			
			{activePanel === 'result' && (
				<>
					<h3 className="text-xl font-medium mb-4 text-primary">{t('resultStep.generationInfo') || 'Generation Details'}</h3>
					
					<div className="generation-details mb-6 flex-1">
						<div className="bg-card/30 backdrop-blur-sm p-4 rounded-xl shadow-sm border border-border/50">
							{prompt && (
								<div className="detail-item mb-4">
									<span
										className="block text-sm font-medium mb-1 text-text">{t('resultStep.prompt')}</span>
									<span
										className="block p-3 bg-background/50 rounded-lg text-text-light text-sm">{prompt}</span>
								</div>
							)}
							
							{negativePrompt && (
								<div className="detail-item mb-4">
									<span
										className="block text-sm font-medium mb-1 text-text">{t('common.negativePrompt')}:</span>
									<span
										className="block p-3 bg-background/50 rounded-lg text-text-light text-sm">{negativePrompt}</span>
								</div>
							)}
							
							{stylePreset && (
								<div className="detail-item">
									<span
										className="block text-sm font-medium mb-1 text-text">{t('common.stylePreset')}:</span>
									<span
										className="block p-3 bg-background/50 rounded-lg text-text-light text-sm">{stylePreset}</span>
								</div>
							)}
						</div>
					</div>
					
					<div className="result-actions flex flex-col gap-3 mt-auto">
						<Button
							variant="default"
							className="w-full"
							onClick={handleDownload}
                            disabled={!resultImage || !resultImage.src} // Disable if no result
						>
							<FaDownload className="mr-2"/> {t('common.download')}
						</Button>
						
						<Button
							variant="outline"
							className="w-full"
							onClick={() => setActivePanel('generate')}
						>
							<FaRedo className="mr-2"/> {t('generateStep.regenerate')}
						</Button>
						
						<Button
							variant="outline"
							className="w-full"
							onClick={handleStartOver}
						>
							<FaRedo className="mr-2"/> {t('common.startOver')}
						</Button>
					</div>
				</>
			)}
		</div>
	);
	
	return (
		<motion.div
			className="furniture-inpainting-app min-h-[calc(100vh-80px)] flex items-stretch"
			initial={{opacity: 0}}
			animate={{opacity: 1}}
			transition={{duration: 0.3}}
		>
			<div className="pt-6 container mx-auto px-4 py-6 flex flex-1">
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 w-full h-[calc(100vh-160px)] overflow-auto">
					{/* Left panel - Instructions and tips */}
					<div className="lg:col-span-3 h-full">
						{renderLeftPanel()}
					</div>
					
					{/* Center panel - Canvas editor or result image */}
					<div className="lg:col-span-6 h-full">
						{renderCenterPanel()}
					</div>
					
					{/* Right panel - Controls and form inputs */}
					<div className="lg:col-span-3 h-full">
						{renderRightPanel()}
					</div>
				</div>
			</div>
		</motion.div>
	);
};

export default FurnitureInpaintingApp;
